import React, {
	createContext,
	useContext,
	useState,
	useEffect,
	useCallback,
} from "react";
import api from "../../services/api";
import { getSocket } from "../../services/socket-io";

// ChatHighlight — resaltador genérico y reusable de chats. Mantiene un mapa
// ticketId → { type, priority, payload } de highlights pending visibles para el
// usuario actual. Initial load por API + actualizaciones en vivo por socket.
// Primer consumidor: E1 rama humano (type "cart_abandoned"). Reusable por otros.
const ChatHighlightContext = createContext({
	highlights: {},
	getHighlight: () => null,
	clearHighlight: () => {},
});

export const useChatHighlight = () => useContext(ChatHighlightContext);

export const ChatHighlightProvider = ({ children }) => {
	const [highlights, setHighlights] = useState({}); // { [ticketId]: { type, priority, payload } }

	// Initial load: highlights pending visibles para este usuario.
	useEffect(() => {
		let cancelled = false;
		api
			.get("/chat-highlights")
			.then(({ data }) => {
				if (cancelled) return;
				const map = {};
				(data || []).forEach(h => {
					const prev = map[h.ticketId];
					if (!prev || (h.priority || 0) > (prev.priority || 0)) {
						map[h.ticketId] = {
							type: h.type,
							priority: h.priority || 0,
							payload: h.payload,
						};
					}
				});
				setHighlights(map);
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, []);

	// Live updates por socket scopeado por empresa.
	useEffect(() => {
		const socket = getSocket();
		if (!socket) return undefined;
		const handler = data => {
			if (!data || !data.ticketId) return;
			if (data.action === "raise") {
				setHighlights(prev => {
					const existing = prev[data.ticketId];
					// gana el de mayor priority si ya hay uno
					if (existing && (existing.priority || 0) >= (data.priority || 0)) {
						return prev;
					}
					return {
						...prev,
						[data.ticketId]: {
							type: data.type,
							priority: data.priority || 0,
							payload: data.payload,
						},
					};
				});
			} else if (data.action === "clear") {
				setHighlights(prev => {
					if (!prev[data.ticketId]) return prev;
					const next = { ...prev };
					delete next[data.ticketId];
					return next;
				});
			}
		};
		socket.on("chatHighlight", handler);
		return () => socket.off("chatHighlight", handler);
	}, []);

	const getHighlight = useCallback(
		ticketId => highlights[ticketId] || null,
		[highlights]
	);

	// Al entrar al chat: quitar optimista + confirmar en backend (status='seen').
	const clearHighlight = useCallback(ticketId => {
		if (!ticketId) return;
		setHighlights(prev => {
			if (!prev[ticketId]) return prev;
			const next = { ...prev };
			delete next[ticketId];
			return next;
		});
		api.post(`/chat-highlights/${ticketId}/clear`).catch(() => {});
	}, []);

	return (
		<ChatHighlightContext.Provider
			value={{ highlights, getHighlight, clearHighlight }}
		>
			{children}
		</ChatHighlightContext.Provider>
	);
};

export default ChatHighlightContext;
